import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { useParams } from 'react-router-dom';
import BootstrapTable from 'react-bootstrap-table-next';
import { api } from '../../utils';

const ProjectDetail = () => {
  const { id } = useParams();
  const [projectDomains, setProjectDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [selectedLinks, setSelectedLinks] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProjectDetail();
  }, [id]);

  const fetchProjectDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`link-building/project-domains/?project=${id}`);
      setProjectDomains(response.data);
    } catch (error) {
      console.error('Error fetching project domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadMessage(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`link-building/projects/${id}/upload_excel/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setUploadMessage(`Success! Created: ${response.data.links_created}, Updated: ${response.data.links_updated}`);
      fetchProjectDetail();
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadMessage(error.response?.data?.error || 'Error uploading file.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const boolFormatter = (cell) => {
    if (cell === true) return <span className="text-success"><i className="fas fa-check" /></span>;
    if (cell === false) return <span className="text-danger"><i className="fas fa-times" /></span>;
    return '-';
  };

  const numberFormatter = (cell) => {
    if (cell == null) return '-';
    return Number(cell).toLocaleString('it-IT');
  };

  const handleDeleteLink = async (linkId, domainName) => {
    if (window.confirm(`Sei sicuro di voler rimuovere il link per "${domainName}" da questo progetto?`)) {
      try {
        await api.delete(`link-building/project-domains/${linkId}/`);
        setSelectedLinks((prev) => prev.filter((selectedId) => selectedId !== linkId));
        fetchProjectDetail();
      } catch (error) {
        console.error('Error deleting link:', error);
        alert(error.response?.data?.error || 'Errore durante la rimozione del link.');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLinks.length === 0) return;

    if (
      window.confirm(
        `Sei sicuro di voler rimuovere i ${selectedLinks.length} link selezionati da questo progetto?`
      )
    ) {
      try {
        await api.post('link-building/project-domains/bulk_delete/', { ids: selectedLinks });
        setSelectedLinks([]);
        fetchProjectDetail();
      } catch (error) {
        console.error('Error deleting links in bulk:', error);
        alert(error.response?.data?.error || "Errore durante l'eliminazione multipla.");
      }
    }
  };

  const actionFormatter = (cell, row) => (
    <Button
      color="danger"
      size="sm"
      outline
      onClick={() => handleDeleteLink(row.id, row.domain?.name)}
      title="Rimuovi dal progetto"
    >
      <i className="fas fa-trash" />
    </Button>
  );

  const columns = [
    { dataField: 'domain.name', text: 'Domain Target', sort: true },
    { dataField: 'url_articolo', text: 'URL Articolo', sort: true },
    { dataField: 'link_interno', text: 'Target (Interno)', sort: true },
    { dataField: 'fornitore.name', text: 'Fornitore', sort: true },
    { dataField: 'domain.ahrefs_dr', text: 'DR', sort: true },
    { dataField: 'domain.ahrefs_org_traffic', text: 'Traffic (Ahrefs)', sort: true, formatter: numberFormatter },
    { dataField: 'domain.ahrefs_refdomains', text: 'Ref. Domains', sort: true, formatter: numberFormatter },
    { dataField: 'status_code', text: 'HTTP Status', sort: true },
    { dataField: 'pagina_indicizzabile', text: 'Indexable', sort: true, formatter: boolFormatter },
    { dataField: 'link_presente', text: 'Link Present', sort: true, formatter: boolFormatter },
    { dataField: 'assenza_attributi', text: 'Dofollow', sort: true, formatter: boolFormatter, title: () => 'Assenza Attributi' },
    { dataField: 'data_di_check', text: 'Last Check', sort: true },
    {
      dataField: 'actions',
      text: 'Azioni',
      isDummyField: true,
      formatter: actionFormatter,
      headerStyle: { width: '80px', textAlign: 'center' },
      style: { textAlign: 'center' },
    },
  ];

  const selectRow = {
    mode: 'checkbox',
    clickToSelect: false,
    selected: selectedLinks,
    onSelect: (row, isSelect) => {
      if (isSelect) {
        setSelectedLinks((prev) => [...prev, row.id]);
      } else {
        setSelectedLinks((prev) => prev.filter((value) => value !== row.id));
      }
    },
    onSelectAll: (isSelect, rows) => {
      const ids = rows.map((row) => row.id);
      if (isSelect) {
        setSelectedLinks((prev) => [...new Set([...prev, ...ids])]);
      } else {
        setSelectedLinks((prev) => prev.filter((value) => !ids.includes(value)));
      }
    },
  };

  return (
    <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
      <div className="container-fluid">
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between align-items-center flex-wrap">
                <h3 className="mb-0">Project Details (ID: {id})</h3>
                <div className="d-flex align-items-center flex-wrap mt-3 mt-md-0">
                  {selectedLinks.length > 0 && (
                    <Button
                      color="danger"
                      size="sm"
                      onClick={handleBulkDelete}
                      className="mr-3"
                      style={{ height: '31px', padding: '0 15px' }}
                    >
                      <i className="fas fa-trash mr-2" /> Elimina Selezionati ({selectedLinks.length})
                    </Button>
                  )}

                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                  />

                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => fileInputRef.current.click()}
                    disabled={uploading}
                    style={{ height: '31px', padding: '0 15px' }}
                  >
                    <i className="fas fa-upload mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Link Excel'}
                  </Button>

                  {uploadMessage && <span className="ml-2 text-sm text-success">{uploadMessage}</span>}
                </div>
              </CardHeader>
              <CardBody>
                <div
                  className="mb-4 text-muted text-sm px-3 py-2 border-left border-info bg-secondary"
                  style={{ borderWidth: '4px !important' }}
                >
                  <i className="fas fa-info-circle mr-2 text-info" />
                  <strong>Istruzioni Excel:</strong> Caricare un file <code>.xlsx</code> o <code>.xls</code>. <br />
                  <span className="ml-4">
                    <strong>Colonne richieste:</strong> <code>url articolo</code>, <code>link interno</code>, <code>fornitore</code>.
                  </span>
                </div>
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <div className="table-responsive">
                    <BootstrapTable
                      bootstrap4
                      keyField="id"
                      data={projectDomains}
                      columns={columns}
                      selectRow={selectRow}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ProjectDetail;
